import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, CalendarRange, Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICES, slugFromKey, fmtDuration } from "@/lib/services";

const GG = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MM = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

// Variazioni eleganti della palette Lavoratore per differenziare i registri (no colori casuali).
const SVC_TINTS = [
  { bg: "#22364D", fg: "#FFFFFF" },
  { bg: "#557A95", fg: "#FFFFFF" },
  { bg: "#3D6B8A", fg: "#FFFFFF" },
  { bg: "#7C9DB6", fg: "#132A3D" },
  { bg: "#8FB4CE", fg: "#132A3D" },
  { bg: "#A9C7DD", fg: "#132A3D" },
];
const IND_TINT = { bg: "#C6A3B4", fg: "#3A2830" }; // ore indirette → accento cipria
// Colori semantici assenze/permessi (riuso logica cromatica esistente).
const LEAVE_COLORS = {
  ferie: { bg: "#D1FAE5", br: "#34D399", fg: "#065F46" },
  malattia: { bg: "#FEE2E2", br: "#F87171", fg: "#991B1B" },
  _def: { bg: "#FEF3C7", br: "#F59E0B", fg: "#92400E" },
};
const leaveColor = (causale) => LEAVE_COLORS[causale] || LEAVE_COLORS._def;

const hhmm = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const toHM = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const fmtDay = (iso) => { const [y, m, d] = iso.split("-").map(Number); return `${d} ${MM[m - 1]}`; };
const svcLabel = (k) => SERVICES[slugFromKey(k)]?.label || k;

export function WeeklyTimeline() {
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const startIso = useMemo(() => {
    const y = monday.getFullYear(), m = String(monday.getMonth() + 1).padStart(2, "0"), d = String(monday.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [monday]);

  useEffect(() => {
    setLoading(true); setSelected(null);
    api.get(`/my/timeline-week?start=${startIso}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [startIso]);

  const isCurrent = startIso === (() => { const t = mondayOf(new Date()); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`; })();
  const shift = (n) => setMonday((prev) => { const x = new Date(prev); x.setDate(x.getDate() + n * 7); return x; });

  // Assegna un indice colore stabile per ciascun service_type presente nella settimana (alternanza ordinata).
  const svcIndex = useMemo(() => {
    const map = {}; let i = 0;
    (data?.blocks || []).forEach((b) => {
      if (b.kind === "work" && !(b.service_type in map)) { map[b.service_type] = i % SVC_TINTS.length; i++; }
    });
    return map;
  }, [data]);

  // Finestra oraria condivisa (dinamica, arrotondata all'ora, min 6h, clamp 5–23).
  const [winStart, winEnd] = useMemo(() => {
    const timed = (data?.blocks || []).filter((b) => hhmm(b.start_time) != null && hhmm(b.end_time) != null);
    if (timed.length === 0) return [8 * 60, 18 * 60];
    let mn = Math.min(...timed.map((b) => hhmm(b.start_time)));
    let mx = Math.max(...timed.map((b) => hhmm(b.end_time)));
    mn = Math.max(5 * 60, Math.floor(mn / 60) * 60);
    mx = Math.min(23 * 60, Math.ceil(mx / 60) * 60);
    if (mx - mn < 360) mx = Math.min(23 * 60, mn + 360);
    return [mn, mx];
  }, [data]);
  const span = winEnd - winStart;
  const hourTicks = useMemo(() => {
    const ticks = []; const step = span > 600 ? 180 : 120;
    for (let t = winStart; t <= winEnd; t += step) ticks.push(t);
    return ticks;
  }, [winStart, winEnd, span]);

  const days = data?.days || [];
  const blocksByDay = (iso) => (data?.blocks || []).filter((b) => b.date === iso && hhmm(b.start_time) != null && hhmm(b.end_time) != null)
    .sort((a, b) => hhmm(a.start_time) - hhmm(b.start_time));
  const alldayByDay = (iso) => (data?.allday || []).filter((a) => a.date === iso);

  const colorOf = (b) => {
    if (b.kind === "leave") { const c = leaveColor(b.causale); return { bg: c.bg, fg: c.fg, br: c.br }; }
    if (b.kind === "indirect") return { ...IND_TINT, br: IND_TINT.bg };
    const t = SVC_TINTS[svcIndex[b.service_type] ?? 0]; return { ...t, br: t.bg };
  };

  return (
    <div data-testid="weekly-timeline" className="mt-2 mb-4">
      <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Timeline settimanale
      </h2>
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
        {/* Navigazione + intervallo */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shrink-0"><CalendarRange className="h-4 w-4 text-[#22364D]" /></div>
            {days.length > 0 && (
              <span className="text-sm font-semibold text-slate-700" data-testid="timeline-range">{fmtDay(days[0])} – {fmtDay(days[6])} {days[6].slice(0, 4)}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {!isCurrent && (
              <button data-testid="timeline-current" onClick={() => setMonday(mondayOf(new Date()))}
                className="text-[11px] font-semibold text-primary px-2 py-1 rounded-lg hover:bg-primary/5">Settimana corrente</button>
            )}
            <button data-testid="timeline-prev" onClick={() => shift(-1)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="timeline-next" onClick={() => shift(1)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-6 text-center">Caricamento…</p>
      ) : (
        <>
          {/* Righello ore condiviso */}
          <div className="relative h-4 ml-14 mb-1">
            {hourTicks.map((t) => (
              <span key={t} className="absolute -translate-x-1/2 text-[9px] font-medium text-slate-400" style={{ left: `${((t - winStart) / span) * 100}%` }}>{toHM(t)}</span>
            ))}
          </div>

          <div className="space-y-1.5">
            {days.map((iso) => {
              const dow = (new Date(iso).getDay() + 6) % 7;
              const timed = blocksByDay(iso);
              const allDay = alldayByDay(iso);
              const fullDayLeave = allDay.find((a) => a.full_day);
              const rowTint = fullDayLeave ? leaveColor(fullDayLeave.causale) : null;
              const weekend = dow >= 5;
              return (
                <div key={iso} className="flex items-stretch gap-2" data-testid={`timeline-day-${iso}`}>
                  <div className={`w-12 shrink-0 flex flex-col items-center justify-center rounded-lg py-1 ${weekend ? "bg-slate-50" : ""}`}>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">{GG[dow]}</span>
                    <span className="text-xs font-bold text-slate-700">{iso.slice(8, 10)}</span>
                  </div>
                  <div className="relative flex-1 h-12 rounded-lg border overflow-hidden"
                    style={{ backgroundColor: rowTint ? rowTint.bg : "#F8FAFC", borderColor: rowTint ? rowTint.br : "#EAEEF2" }}>
                    {/* gridlines */}
                    {!rowTint && hourTicks.map((t) => (
                      <span key={t} className="absolute top-0 bottom-0 w-px bg-slate-200/70" style={{ left: `${((t - winStart) / span) * 100}%` }} />
                    ))}
                    {/* full-day leave label */}
                    {rowTint && (
                      <div className="absolute inset-0 flex items-center px-3">
                        <span className="text-xs font-bold" style={{ color: rowTint.fg }}>{fullDayLeave.causale_label}</span>
                      </div>
                    )}
                    {/* timed blocks */}
                    {!rowTint && timed.map((b, bi) => {
                      const s = hhmm(b.start_time), e = hhmm(b.end_time);
                      const left = Math.max(0, ((s - winStart) / span) * 100);
                      const width = Math.max(9, ((e - s) / span) * 100);
                      const col = colorOf(b);
                      return (
                        <button key={bi} data-testid={`timeline-block-${iso}-${bi}`} onClick={() => setSelected(b)}
                          className="absolute top-1 bottom-1 rounded-md px-1.5 flex flex-col justify-center overflow-hidden text-left active:scale-[0.98] transition-transform"
                          style={{ left: `${left}%`, width: `calc(${width}% - 2px)`, backgroundColor: col.bg }}>
                          <span className="text-[9px] font-bold leading-none truncate" style={{ color: col.fg }}>{b.start_time}</span>
                          <span className="text-[9px] leading-tight truncate opacity-90" style={{ color: col.fg }}>
                            {b.kind === "work" ? svcLabel(b.service_type) : b.kind === "indirect" ? "Ore ind." : b.titolo}
                          </span>
                        </button>
                      );
                    })}
                    {!rowTint && timed.length === 0 && allDay.length === 0 && (
                      <span className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-300">—</span>
                    )}
                    {/* chip permessi orari senza posizione? già gestiti come timed. Chip allday non full-day */}
                    {!rowTint && allDay.filter((a) => !a.full_day).map((a, ai) => {
                      const c = leaveColor(a.causale);
                      return (
                        <span key={`ad-${ai}`} className="absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: c.bg, color: c.fg, border: `1px solid ${c.br}` }}>{a.causale_label}</span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legenda compatta */}
          <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t border-slate-100">
            <LegendDot color="#22364D" label="Servizi" />
            <LegendDot color={IND_TINT.bg} label="Ore indirette" />
            <LegendDot color={LEAVE_COLORS.ferie.br} label="Ferie" />
            <LegendDot color={LEAVE_COLORS.malattia.br} label="Malattia" />
            <LegendDot color={LEAVE_COLORS._def.br} label="Permessi" />
          </div>
        </>
      )}

      {/* Dettaglio blocco */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
            data-testid="timeline-detail" className="mt-3 rounded-xl border border-slate-200 bg-[#F8FAFC] p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-heading font-bold text-slate-800 leading-tight">
                  {selected.kind === "work" ? svcLabel(selected.service_type) : selected.kind === "indirect" ? "Ore indirette" : selected.causale_label}
                </p>
                {(selected.titolo && selected.kind !== "leave") && <p className="text-sm text-slate-600 truncate">{selected.titolo}{selected.sottotitolo ? ` · ${selected.sottotitolo}` : ""}</p>}
                {selected.role_or_task && <p className="text-xs text-slate-500">{selected.role_or_task}</p>}
                <p className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  <Clock className="h-3.5 w-3.5" />{selected.start_time}–{selected.end_time}
                  {selected.duration_minutes != null && <span className="text-slate-400 font-normal">· {fmtDuration(selected.duration_minutes)}</span>}
                </p>
              </div>
              <button data-testid="timeline-detail-close" onClick={() => setSelected(null)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 shrink-0"><X className="h-4 w-4" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />{label}
    </span>
  );
}
